/**
 * The onboarding screening and the applicability suggestions on the Obligations tab of an entity
 * (ALC-4767). Namespace `entityObligationSuggestions`.
 */
export const entityObligationSuggestionsPtBr = {
  screening: {
    title: 'Triagem de implantação',
    description:
      'Responda os fatos que mudam as obrigações da empresa. As respostas alimentam as sugestões do ERP; nenhuma resposta decide uma obrigação sozinha.',
    unanswered_one: '{{count}} pergunta sem resposta',
    unanswered_other: '{{count}} perguntas sem resposta',
    complete: 'Todas as perguntas foram respondidas.',
    unansweredBadge: 'Sem resposta',
    yes: 'Sim',
    no: 'Não',
    answeredAt: 'Respondida em {{date}}',
    companyQuestion: 'Vale para toda a empresa',
    establishmentQuestion: 'Vale para este estabelecimento',
    readOnlyCompany:
      'As perguntas sobre a empresa são respondidas na matriz. Aqui elas aparecem só para leitura.',
    noCompanyAccess:
      'Para responder as perguntas sobre a empresa, é preciso ter acesso a todos os estabelecimentos dela. Peça a quem administra o cadastro da empresa.',
    readOnlyNoPermission: 'Somente leitura: você não tem permissão para responder a triagem.',
    save: 'Salvar respostas',
    saving: 'Salvando...',
    saveError: 'Não foi possível salvar as respostas. Tente novamente.',
    loadError: 'Não foi possível carregar a triagem desta entidade.',
    groups: {
      PAYROLL_AND_WITHHOLDINGS: 'Folha, retenções e dividendos',
      SERVICES_ABROAD: 'Serviços com o exterior',
      GOODS_AND_STATE_TAXES: 'Venda de bens e tributos estaduais',
      MUNICIPAL: 'ISS por município',
      SPECIAL_REGIME: 'Regimes especiais',
      CORPORATE_EVENT: 'Eventos societários',
    },
    questions: {
      HAS_PAYROLL: 'A empresa tem folha de pagamento (empregados ou pró-labore)?',
      MAKES_WITHHOLDINGS:
        'A empresa faz retenções na fonte (IRRF, contribuições, INSS) em pagamentos a terceiros?',
      PAYS_DIVIDENDS: 'A empresa paga lucros ou dividendos?',
      IMPORTS_SERVICES: 'A empresa importa serviços (contrata prestadores do exterior)?',
      IMPORTS_SAAS_CLOUD: 'A empresa importa SaaS ou serviços de nuvem do exterior?',
      EXPORTS_SERVICES: 'A empresa exporta serviços (atende clientes do exterior)?',
      EXPORTS_SAAS_CLOUD: 'A empresa exporta SaaS ou serviços de nuvem?',
      SELLS_GOODS: 'A empresa vende bens (emite NF-e de mercadorias)?',
      HAS_ICMS_IPI_REGISTRATION:
        'A empresa tem inscrição estadual ou é contribuinte de ICMS ou de IPI?',
      ISS_DF: 'Este estabelecimento recolhe ISS no Distrito Federal?',
      SPECIAL_IMMUNE_OR_EXEMPT: 'A empresa é imune ou isenta?',
      SPECIAL_COOPERATIVE: 'A empresa é uma cooperativa?',
      SPECIAL_FINANCIAL_INSTITUTION: 'A empresa é uma instituição financeira?',
      EVENT_EXTINCTION: 'Há extinção da empresa prevista?',
      EVENT_SPLIT: 'Há cisão prevista?',
      EVENT_MERGER: 'Há fusão prevista?',
      EVENT_INCORPORATION: 'Há incorporação prevista?',
    },
  },
  suggestions: {
    title: 'Sugestões do ERP',
    description:
      'O ERP propõe uma decisão para cada obrigação a partir do regime, do tratamento de CBS e IBS e da triagem. Nada é gravado até você aceitar.',
    tableLabel: 'Sugestões de aplicabilidade',
    loadError: 'Não foi possível carregar as sugestões desta entidade.',
    summary: {
      acceptable_one: '{{count}} sugestão para aceitar',
      acceptable_other: '{{count}} sugestões para aceitar',
      conflicts_one: '{{count}} decisão para revisar',
      conflicts_other: '{{count}} decisões para revisar',
      none: 'Nenhuma sugestão para aceitar agora.',
    },
    unanswered_one:
      'Falta {{count}} resposta da triagem: as obrigações que dependem dela ficam pendentes.',
    unanswered_other:
      'Faltam {{count}} respostas da triagem: as obrigações que dependem delas ficam pendentes.',
    profile: {
      NO_TAXPAYER:
        'Esta entidade não tem contribuinte federal, então o ERP não sugere as obrigações federais. Informe o contribuinte em "Informações básicas".',
      NO_FISCAL_HISTORY:
        'O regime de IRPJ do contribuinte ainda não está registrado para esta data. Registre-o na aba "Regime & Alíquotas" para o ERP sugerir as obrigações que dependem dele.',
      resolved: 'Perfil usado: {{regime}}, CBS {{cbs}}, IBS {{ibs}}.',
      notRegistered: 'não registrado',
    },
    regime: {
      SIMPLES_NACIONAL: 'Simples Nacional',
      LUCRO_PRESUMIDO: 'Lucro Presumido',
      LUCRO_REAL: 'Lucro Real',
    },
    treatment: {
      SIMPLES_INSIDE_DAS: 'no DAS',
      SIMPLES_REGULAR_OPTION: 'regular por opção',
      REGULAR: 'regular',
      SIMPLES_SUBLIMIT_EXCEEDED: 'regular por sublimite excedido',
      OUT_OF_SCOPE: 'fora do escopo',
    },
    columns: {
      obligation: 'Obrigação',
      suggestion: 'Sugestão',
      current: 'Decisão atual',
      reason: 'Motivo',
    },
    noCurrent: 'Sem decisão',
    conflict: 'Revisar decisão',
    conflictHelp:
      'A decisão em vigor difere do que o ERP sugere hoje: o perfil ou a triagem mudaram.',
    readOnlyRow: 'Decidida na matriz',
    noCompanyAccessRow: 'Sem acesso a toda a empresa',
    waiverBasis: 'Fundamento: {{basis}}',
    acceptNew: 'Aceitar as novas sugestões ({{count}})',
    acceptSelected: 'Aceitar sugestão',
    noun: { one: 'sugestão', other: 'sugestões' },
    accepting: 'Aceitando...',
    readOnlyNoPermission: 'Somente leitura: você não tem permissão para aceitar sugestões.',
    empty: {
      title: 'Nada a aceitar ou revisar',
      description:
        'Quando a triagem e o perfil permitirem uma sugestão diferente da decisão em vigor, ela aparece aqui.',
    },
    failures: {
      title: 'Algumas sugestões não foram aceitas',
      item: '{{name}}: {{message}}',
    },
    acceptedSummary_one: '{{count}} sugestão aceita e gravada como decisão.',
    acceptedSummary_other: '{{count}} sugestões aceitas e gravadas como decisão.',
    errors: {
      OBLIGATION_NOT_FOUND: 'Esta obrigação não está no catálogo. Atualize a página.',
      OBLIGATION_SUGGESTION_NOT_DECISIVE:
        'O ERP ainda não tem uma decisão a sugerir. Responda a triagem ou decida manualmente.',
      OBLIGATION_SUGGESTION_CHANGED:
        'A sugestão mudou desde que você a viu. Revise a nova sugestão.',
      OBLIGATION_APPLICABILITY_PERIOD_LOCKED:
        'Uma obrigação já transmitida usa esta decisão. Decida com data de início posterior ao período entregue.',
      ESTABLISHMENT_PROFILE_HEAD_OFFICE_ONLY:
        'Obrigações federais são decididas na matriz. Abra a matriz para aceitar.',
      fallback: 'Não foi possível aceitar esta sugestão.',
    },
    acceptError: 'Não foi possível aceitar as sugestões. Tente novamente.',
  },
} as const;

type Widen<T> = T extends string ? string : { [K in keyof T]: Widen<T[K]> };

export const entityObligationSuggestionsEn: Widen<typeof entityObligationSuggestionsPtBr> = {
  screening: {
    title: 'Onboarding screening',
    description:
      'Answer the facts that change the obligations of the company. The answers feed the ERP suggestions; no answer decides an obligation on its own.',
    unanswered_one: '{{count}} unanswered question',
    unanswered_other: '{{count}} unanswered questions',
    complete: 'Every question has been answered.',
    unansweredBadge: 'Unanswered',
    yes: 'Yes',
    no: 'No',
    answeredAt: 'Answered on {{date}}',
    companyQuestion: 'Applies to the whole company',
    establishmentQuestion: 'Applies to this establishment',
    readOnlyCompany:
      'Questions about the company are answered on the head office. Here they are read-only.',
    noCompanyAccess:
      'To answer the questions about the company, you need access to every establishment of it. Ask the person who manages the company record.',
    readOnlyNoPermission: 'Read-only: you do not have permission to answer the screening.',
    save: 'Save answers',
    saving: 'Saving...',
    saveError: 'Could not save the answers. Try again.',
    loadError: 'Could not load the screening of this entity.',
    groups: {
      PAYROLL_AND_WITHHOLDINGS: 'Payroll, withholdings and dividends',
      SERVICES_ABROAD: 'Services with abroad',
      GOODS_AND_STATE_TAXES: 'Goods sales and state taxes',
      MUNICIPAL: 'ISS by municipality',
      SPECIAL_REGIME: 'Special regimes',
      CORPORATE_EVENT: 'Corporate events',
    },
    questions: {
      HAS_PAYROLL: 'Does the company have payroll (employees or owner compensation)?',
      MAKES_WITHHOLDINGS:
        'Does the company withhold taxes at source (income tax, contributions, social security) on payments to third parties?',
      PAYS_DIVIDENDS: 'Does the company pay profits or dividends?',
      IMPORTS_SERVICES: 'Does the company import services (hire providers abroad)?',
      IMPORTS_SAAS_CLOUD: 'Does the company import SaaS or cloud services from abroad?',
      EXPORTS_SERVICES: 'Does the company export services (serve customers abroad)?',
      EXPORTS_SAAS_CLOUD: 'Does the company export SaaS or cloud services?',
      SELLS_GOODS: 'Does the company sell goods (issue NF-e for merchandise)?',
      HAS_ICMS_IPI_REGISTRATION:
        'Does the company have a state registration or is it an ICMS or IPI taxpayer?',
      ISS_DF: 'Does this establishment pay ISS in the Federal District?',
      SPECIAL_IMMUNE_OR_EXEMPT: 'Is the company immune or exempt?',
      SPECIAL_COOPERATIVE: 'Is the company a cooperative?',
      SPECIAL_FINANCIAL_INSTITUTION: 'Is the company a financial institution?',
      EVENT_EXTINCTION: 'Is the extinction of the company planned?',
      EVENT_SPLIT: 'Is a split planned?',
      EVENT_MERGER: 'Is a merger planned?',
      EVENT_INCORPORATION: 'Is an incorporation planned?',
    },
  },
  suggestions: {
    title: 'ERP suggestions',
    description:
      'The ERP proposes a decision for each obligation from the regime, the CBS and IBS treatment and the screening. Nothing is saved until you accept.',
    tableLabel: 'Applicability suggestions',
    loadError: 'Could not load the suggestions of this entity.',
    summary: {
      acceptable_one: '{{count}} suggestion to accept',
      acceptable_other: '{{count}} suggestions to accept',
      conflicts_one: '{{count}} decision to review',
      conflicts_other: '{{count}} decisions to review',
      none: 'No suggestion to accept right now.',
    },
    unanswered_one:
      '{{count}} screening answer is missing: the obligations that depend on it stay pending.',
    unanswered_other:
      '{{count}} screening answers are missing: the obligations that depend on them stay pending.',
    profile: {
      NO_TAXPAYER:
        'This entity has no federal Taxpayer, so the ERP does not suggest federal obligations. Set the Taxpayer in "Basic information".',
      NO_FISCAL_HISTORY:
        'The IRPJ regime of the Taxpayer is not registered for this date. Register it on the "Regime & Rates" tab so the ERP can suggest the obligations that depend on it.',
      resolved: 'Profile used: {{regime}}, CBS {{cbs}}, IBS {{ibs}}.',
      notRegistered: 'not registered',
    },
    regime: {
      SIMPLES_NACIONAL: 'Simples Nacional',
      LUCRO_PRESUMIDO: 'Lucro Presumido',
      LUCRO_REAL: 'Lucro Real',
    },
    treatment: {
      SIMPLES_INSIDE_DAS: 'inside the DAS',
      SIMPLES_REGULAR_OPTION: 'regular by option',
      REGULAR: 'regular',
      SIMPLES_SUBLIMIT_EXCEEDED: 'regular, sublimit exceeded',
      OUT_OF_SCOPE: 'out of scope',
    },
    columns: {
      obligation: 'Obligation',
      suggestion: 'Suggestion',
      current: 'Current decision',
      reason: 'Reason',
    },
    noCurrent: 'No decision',
    conflict: 'Review decision',
    conflictHelp:
      'The decision in force differs from what the ERP suggests today: the profile or the screening changed.',
    readOnlyRow: 'Decided on the head office',
    noCompanyAccessRow: 'No access to the whole company',
    waiverBasis: 'Basis: {{basis}}',
    acceptNew: 'Accept the new suggestions ({{count}})',
    acceptSelected: 'Accept suggestion',
    noun: { one: 'suggestion', other: 'suggestions' },
    accepting: 'Accepting...',
    readOnlyNoPermission: 'Read-only: you do not have permission to accept suggestions.',
    empty: {
      title: 'Nothing to accept or review',
      description:
        'When the screening and the profile allow a suggestion that differs from the decision in force, it shows up here.',
    },
    failures: {
      title: 'Some suggestions were not accepted',
      item: '{{name}}: {{message}}',
    },
    acceptedSummary_one: '{{count}} suggestion accepted and recorded as a decision.',
    acceptedSummary_other: '{{count}} suggestions accepted and recorded as decisions.',
    errors: {
      OBLIGATION_NOT_FOUND: 'This obligation is not in the catalog. Refresh the page.',
      OBLIGATION_SUGGESTION_NOT_DECISIVE:
        'The ERP has no decision to suggest yet. Answer the screening or decide manually.',
      OBLIGATION_SUGGESTION_CHANGED: 'The suggestion changed since you saw it. Review the new one.',
      OBLIGATION_APPLICABILITY_PERIOD_LOCKED:
        'An obligation already transmitted uses this decision. Decide with a start date after the delivered period.',
      ESTABLISHMENT_PROFILE_HEAD_OFFICE_ONLY:
        'Federal obligations are decided on the head office. Open it to accept.',
      fallback: 'Could not accept this suggestion.',
    },
    acceptError: 'Could not accept the suggestions. Try again.',
  },
};
